/* W12 — Obligation & Recurrence Engine : campagnes périodiques + incidents.
 * NOAM impose le calendrier opérationnel — chaque période génère une obligation nominative. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { CalendarClock, Plus, AlertOctagon, Loader2 } from "lucide-react";

const FREQ_LABEL = { weekly: "Hebdomadaire", monthly: "Mensuelle", quarterly: "Trimestrielle", semiannual: "Semestrielle", annual: "Annuelle" };
const KIND_LABEL = { control_test: "Test de contrôle", attestation: "Attestation", review: "Revue", committee: "Comité" };

export function CampaignsPanel() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ title: "", kind: "control_test", frequency: "quarterly", owner_email: "", consequence: "" });
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/workspace/campaigns").then((r) => setData(r.data)).catch((e) => notify.error(fmtError(e)));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.title.trim() || !f.owner_email.trim()) return notify.error("Titre et responsable requis.");
    setBusy(true);
    try {
      await api.post("/workspace/campaigns", { ...f, consequence: f.consequence.trim() || null });
      notify.success("Campagne créée — première obligation générée immédiatement.");
      setF({ title: "", kind: "control_test", frequency: "quarterly", owner_email: "", consequence: "" });
      load();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  if (!data) return <div className="flex justify-center py-8 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  return (
    <div className="space-y-3" data-testid="campaigns-panel">
      <div className="text-[10px] px-2.5 py-1.5 rounded border border-white/10 text-zinc-400 bg-white/[0.02]">
        Une campagne génère automatiquement, à chaque période, une obligation nominative avec échéance
        et conséquence. L'exécution avec preuve alimente le dossier comité — le calendrier opérationnel
        commence à passer par NOAM.
      </div>
      {(data.items || []).map((c) => (
        <div key={c.id} className="noam-card p-3 text-xs" data-testid={`campaign-${c.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <CalendarClock className="w-3.5 h-3.5 text-[#1D9E75]" />
            <span className="text-zinc-200 flex-1 min-w-[160px]">{c.title}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-400">{KIND_LABEL[c.kind] || c.kind}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-400">{FREQ_LABEL[c.frequency]}</span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${c.active ? "border-[#1D9E75]/40 text-[#1D9E75]" : "border-white/15 text-zinc-500"}`}>{c.active ? "Active" : "Suspendue"}</span>
          </div>
          <div className="text-[10px] text-zinc-500 mt-1">
            Responsable : {c.owner_email} · prochaine occurrence {c.next_run_at ? new Date(c.next_run_at).toLocaleDateString("fr-CH") : "—"} ·
            <b className="text-[#1D9E75]"> {c.executed} exécutée(s) avec preuve</b>{c.pending > 0 && <span className="text-[#EF9F27]"> · {c.pending} en attente</span>}
          </div>
        </div>
      ))}
      <div className="p-2.5 rounded border border-dashed border-white/10 space-y-1.5">
        <div className="flex gap-1.5 flex-wrap">
          <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Titre de la campagne *" className="noam-input text-[10px] flex-1 min-w-[200px]" data-testid="campaign-title-input" />
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="noam-input text-[10px] w-40" data-testid="campaign-kind-select">
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <select value={f.frequency} onChange={(e) => setF({ ...f, frequency: e.target.value })} className="noam-input text-[10px] w-36" data-testid="campaign-freq-select">
            {Object.entries(FREQ_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <input value={f.owner_email} onChange={(e) => setF({ ...f, owner_email: e.target.value })} placeholder="Responsable (email) *" className="noam-input text-[10px] w-56" data-testid="campaign-owner-input" />
          <input value={f.consequence} onChange={(e) => setF({ ...f, consequence: e.target.value })} placeholder="Conséquence si non exécutée" className="noam-input text-[10px] flex-1 min-w-[180px]" />
          <button onClick={create} disabled={busy} className="btn-primary text-[10px]" data-testid="campaign-create-btn"><Plus className="w-3 h-3" /> Créer la campagne</button>
        </div>
      </div>
    </div>
  );
}

export function IncidentsPanel() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ title: "", severity: 3, description: "" });
  const [closingId, setClosingId] = useState(null);
  const [resolution, setResolution] = useState("");
  const load = () => api.get("/workspace/risk-incidents").then((r) => setData(r.data)).catch((e) => notify.error(fmtError(e)));
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!f.title.trim()) return notify.error("Titre requis.");
    try {
      await api.post("/workspace/risk-incidents", { title: f.title.trim(), severity: Number(f.severity), description: f.description.trim() || null });
      notify.success("Incident enregistré — la réalité du risque alimente le registre.");
      setF({ title: "", severity: 3, description: "" });
      load();
    } catch (e) { notify.error(fmtError(e)); }
  };
  const close = async (id) => {
    if (!resolution.trim()) return notify.error("Résolution obligatoire (trace d'audit).");
    try {
      await api.post(`/workspace/risk-incidents/${id}/close`, { resolution: resolution.trim() });
      setClosingId(null); setResolution("");
      load();
    } catch (e) { notify.error(fmtError(e)); }
  };
  if (!data) return <div className="flex justify-center py-8 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  return (
    <div className="space-y-2" data-testid="incidents-panel">
      <div className="text-[10px] text-zinc-500">{data.open} incident(s) ouvert(s) — les incidents sont la matière première de la calibration risque réelle.</div>
      {(data.items || []).map((i) => (
        <div key={i.id} className={`p-2.5 rounded border text-xs ${i.status === "open" ? "border-[#E24B4A]/30 bg-[#E24B4A]/5" : "border-white/5 bg-white/[0.02]"}`} data-testid={`incident-${i.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <AlertOctagon className={`w-3.5 h-3.5 ${i.status === "open" ? "text-[#E24B4A]" : "text-zinc-500"}`} />
            <span className="text-zinc-200 flex-1">{i.title}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-white/15 text-zinc-400">Sévérité {i.severity}/5</span>
            {i.status === "open"
              ? <button onClick={() => { setClosingId(closingId === i.id ? null : i.id); setResolution(""); }} className="btn-ghost text-[10px] border border-white/10" data-testid={`incident-close-${i.id}`}>Clore avec résolution</button>
              : <span className="text-[9px] text-[#1D9E75]">Clos</span>}
          </div>
          {closingId === i.id && (
            <div className="flex gap-1.5 mt-1.5">
              <input value={resolution} onChange={(e) => setResolution(e.target.value)} placeholder="Résolution (obligatoire — trace d'audit) *" className="noam-input text-[10px] flex-1" data-testid={`incident-resolution-${i.id}`} />
              <button onClick={() => close(i.id)} className="btn-primary text-[10px]" data-testid={`incident-close-confirm-${i.id}`}>Clore</button>
            </div>
          )}
          <div className="text-[10px] text-zinc-500 mt-0.5">
            {new Date(i.occurred_at).toLocaleDateString("fr-CH")}{i.description ? ` · ${i.description}` : ""}{i.resolution ? ` · résolution : ${i.resolution}` : ""}
          </div>
        </div>
      ))}
      <div className="p-2.5 rounded border border-dashed border-white/10 flex gap-1.5 flex-wrap">
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Incident / événement de risque *" className="noam-input text-[10px] flex-1 min-w-[200px]" data-testid="incident-title-input" />
        <select value={f.severity} onChange={(e) => setF({ ...f, severity: e.target.value })} className="noam-input text-[10px] w-28" data-testid="incident-severity-select">
          {[1, 2, 3, 4, 5].map((s) => <option key={s} value={s}>Sévérité {s}</option>)}
        </select>
        <input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Description" className="noam-input text-[10px] flex-1 min-w-[160px]" />
        <button onClick={create} className="btn-primary text-[10px]" data-testid="incident-create-btn"><Plus className="w-3 h-3" /> Enregistrer</button>
      </div>
    </div>
  );
}
